import { useEffect, useState } from "react";

import { RolesApi } from "@/Administration/Api/Roles/RolesApi";
import {
    NotUsingRolePermissionsWarningComponent
} from "@/Administration/Components/Roles/Field/NotUsingRolePermissionsWarningComponent";
import { RoleListComponent } from "@/Administration/Components/Roles/Field/RoleListComponent";
import { RoleHeaderModel } from "@/Administration/Models/Role/RoleHeaderModel";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { FormMode } from "@/Common/Models/UI/Enums";

import styles from "@/Administration/Components/Roles/Field/RolesListFieldComponent.module.scss";

interface IRoleListFieldComponentProps {
    dataCy: string;
    selectedRolesList: string[];
    setSelectedRoles: (list: string[]) => void;
    formMode: FormMode;
    showWarning: boolean;
}

function RoleListFieldComponent(props: Readonly<IRoleListFieldComponentProps>): JSX.Element {
    const { getText } = useTranslation();
    const [searchText, setSearchText] = useState<string>();
    const [searchableRoles, setSearchableRoles] = useState<RoleHeaderModel[]>([]);
    const { selectedRolesList, setSelectedRoles } = props;

    useEffect(() => {
        refreshRoleHeaders();
    }, []);

    const refreshRoleHeaders = async (): Promise<void> => {
        const response = await RolesApi.GetCachedRoleHeadersAsync();
        setSearchableRoles(response.RoleHeaders);
    };

    const onRemoveClick = (Id: string): void => {
        const nextSelectedRoles = selectedRolesList?.filter(role => role !== Id);
        setSelectedRoles(nextSelectedRoles);
    };

    const handleRoleSelected = (_, role: RoleHeaderModel): void => {
        setSearchText("");

        let selectedRoles = [role.Id as string];
        if (selectedRolesList)
            selectedRoles = selectedRoles.concat(selectedRolesList);

        setSelectedRoles(selectedRoles);
    };

    const formIsInEditMode = props.formMode == FormMode.Edit;
    const wrapperClassName = formIsInEditMode ? `${styles.roleSearchContainer} ${styles.edit}` : styles.roleSearchContainer;

    return (
        <div className={wrapperClassName} data-cy={props.dataCy}>
            {formIsInEditMode && (
                <TextboxControl
                    className={styles.addRoleSearchInput}
                    debounceMs={200}
                    value={searchText}
                    autoCompleteItems={searchableRoles.filter(role => !selectedRolesList?.any(roleId => role.Id === roleId)).map(item => new SearchableItemModelT<RoleHeaderModel>(item.Name, item.Id, item)).toImmutable()}
                    onAutoCompleteSelectCallBack={handleRoleSelected}
                    onAutoCompleteSelectClearTextBox
                    listWidth="100%"
                    placeholder={getText("SearchLabel")}
                    onChange={searchText => setSearchText(searchText)}
                    valid
                    dataCy={`${props.dataCy}-role-search-input`}
                />
            )}

            <RoleListComponent
                onRemoveRoleClick={contactFacetId => onRemoveClick(contactFacetId)}
                roles={searchableRoles.filter(role => selectedRolesList.contains(role.Id))}
                formMode={props.formMode}
                dataCy={props.dataCy}
            />

            {props.showWarning &&
                <NotUsingRolePermissionsWarningComponent />
            }
        </div>
    );
}

export { RoleListFieldComponent };
